import type { QueryFunction } from '@tanstack/svelte-query'
import camelcaseKeys from 'camelcase-keys'

import { handleFetch } from '@utils/fetch'
import { queryClient } from '@utils/svelte-query-client'

enum DatasetQARunStatus {
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  STARTED = 'STARTED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  REVOKED = 'REVOKED',
  IGNORED = 'IGNORED',
  REJECTED = 'REJECTED',
  AWAITING_MANUAL_APPROVAL = 'AWAITING_MANUAL_APPROVAL',
}

const displayRunStatus = {
  [DatasetQARunStatus.PENDING]: 'Pending',
  [DatasetQARunStatus.STARTED]: 'Started',
  [DatasetQARunStatus.SUCCESS]: 'Success',
  [DatasetQARunStatus.FAILURE]: 'Failure',
  [DatasetQARunStatus.RETRY]: 'Retry',
  [DatasetQARunStatus.REVOKED]: 'Cancelled',
  [DatasetQARunStatus.REJECTED]: 'Rejected',
  [DatasetQARunStatus.AWAITING_MANUAL_APPROVAL]: 'Awaiting approval',
}
const displayRunStatusSubtitle = {
  [DatasetQARunStatus.PENDING]: 'Run not yet started: Provisioning compute',
  [DatasetQARunStatus.STARTED]: 'Run in progress',
  [DatasetQARunStatus.SUCCESS]: '',
  [DatasetQARunStatus.FAILURE]: 'Run failed. Please check your dataset',
  [DatasetQARunStatus.RETRY]: 'Run retrying',
  [DatasetQARunStatus.REVOKED]: 'Run cancelled',
  [DatasetQARunStatus.REJECTED]: 'Run rejected. Try re-running',
  [DatasetQARunStatus.AWAITING_MANUAL_APPROVAL]: 'Run awaiting approval',
}

export interface IClassificationRunArgs {
  imgSize: readonly [number, number]
  upsample: boolean
}

export interface IODRunArgs extends IClassificationRunArgs {
  minAbsBboxSize: number
  minAbsBboxArea: number
  minRelBboxSize: number
  minRelBboxArea: number
  cropRatio: number | null
  addMaskChannel: boolean
}

interface IDatasetQARun {
  id: number
  name: string
  runId: string
  completed: boolean
  approved: boolean
  datasetId: number
  status: DatasetQARunStatus
  preProcessProgress: number
  trainingProgress: number
  postProcessProgress: number
  clearmlTaskId: string
  mlflowRunId: string
  runArgs: IClassificationRunArgs | IODRunArgs | null
  createdAt: Date
}
type IRawDatasetQARun = Omit<IDatasetQARun, 'createdAt' | 'runArgs'> & {
  createdAt: string
  runArgs: IClassificationRunArgs | IODRunArgs | null
}

const parseDatasetQA = (datasetQARun: IRawDatasetQARun): IDatasetQARun => ({
  ...datasetQARun,
  createdAt: new Date(datasetQARun.createdAt),
})

const datasetQARunsQuery: ({
  archived,
}: {
  archived?: boolean
}) => QueryFunction<Map<number, IDatasetQARun>> =
  ({ archived = false }: { archived?: boolean }) =>
  async ({ signal }) => {
    try {
      const datasetQARuns: IRawDatasetQARun[] = camelcaseKeys(
        await (
          await handleFetch(`/dataset-qa/run/list?archived=${archived}`, {
            signal,
          })
        ).json(),
        { deep: true },
      )
      const datasetQARunMap = datasetQARuns.reduce((acc, datasetQARun) => {
        acc.set(datasetQARun.id, parseDatasetQA(datasetQARun))
        return acc
      }, new Map<number, IDatasetQARun>())
      return datasetQARunMap
    } catch (err) {
      console.error('Failed to load dataset QA runs: ', err)
      throw err
    }
  }

const datasetQARunsQueryKey = (archived?: boolean) => ['dataset-qa-runs', archived]

/**
 * Loads the dataset QA runs from the server.
 * Invalidates the query cache to ensure the data is fresh.
 */
const loadDatasetQARuns = async ({ archived = false }: { archived?: boolean } = {}) => {
  await queryClient.invalidateQueries({ queryKey: datasetQARunsQueryKey(archived) })
  await queryClient.fetchQuery({
    queryKey: datasetQARunsQueryKey(archived),
    queryFn: datasetQARunsQuery({ archived }),
  })
}

export type { IDatasetQARun }
export {
  datasetQARunsQuery,
  datasetQARunsQueryKey,
  DatasetQARunStatus,
  displayRunStatus,
  displayRunStatusSubtitle,
  loadDatasetQARuns,
}
